import type React from "react";
import { useMemo, useState } from "react";

import dayjs from "dayjs";

import { SWReactIcons } from "@similarweb/icons";
import { PlainTooltip } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import { AssetIcon } from "app/pages/digital-suite/home-page/components/CustomAssets/AssetIcon";
import {
    Layout,
    TextContainer,
    Title,
    Text,
    FooterContainer,
    HoverFooterWrapper,
    SharedIconWrapper,
    AddedDate,
    ArrowIconWrapper,
    SharedTooltipContent,
} from "app/pages/digital-suite/home-page/components/CustomAssets/Assets/Asset/styledComponents";
import { AssetContainer } from "app/pages/digital-suite/home-page/components/CustomAssets/Assets/styledComponents";
import type { AssetProps } from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { AssetName } from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { allTrackers } from "app/services/track/track";
import type { IShareUser } from "app/sharing/SharingService";

const TRACKING_EVENT_NAME_PREFIX = {
    [AssetName.Dashboard]: "My Assets/view dashboard",
    [AssetName.KeywordLists]: "My Assets/view keywordlist",
};

const TITLES = {
    [AssetName.Dashboard]: "digitalmarketing.homepage.custom_assets.asset_title.dashboard",
    [AssetName.KeywordLists]: "digitalmarketing.homepage.custom_assets.asset_title.keywordList",
};

type AssetPropsExtension = {
    index: number;
    maxWidth?: number;
};

export const Asset: React.FC<AssetProps & AssetPropsExtension> = (props) => {
    const { addedTime, maxWidth, type, index, onClick, name, hoverFooter, lastSeen } = props;
    const i18n = i18nFilter();
    const [hovered, setHovered] = useState<boolean>(false);
    const timeAgo = useMemo<string>(() => {
        const date = lastSeen ?? addedTime;
        return dayjs(date).fromNow().toString();
    }, [addedTime, lastSeen]);

    let owner: IShareUser;
    if ("owner" in props) {
        owner = props.owner;
    }

    return (
        <AssetContainer
            data-automation="custom-asset-container"
            onMouseOver={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            maxWidth={maxWidth}
            onClick={() => {
                allTrackers.dispatchTrackEvent("Button", "click", `${TRACKING_EVENT_NAME_PREFIX[type]}/${index + 1}`);

                onClick();
            }}
        >
            <Layout>
                <AssetIcon type={type} />
                <TextContainer>
                    <Title>{i18n(TITLES[type])}</Title>
                    <Text data-automation="custom-asset-name">{name}</Text>
                </TextContainer>
                <FooterContainer>
                    {owner ? (
                        <PlainTooltip
                            tooltipContent={
                                <SharedTooltipContent>
                                    <div>{i18n("digitalmarketing.homepage.custom_assets.sharedBy")}</div>
                                    <div>{`${owner.firstName} ${owner.lastName}`}</div>
                                </SharedTooltipContent>
                            }
                            placement={"bottom"}
                            debounce={200}
                        >
                            <SharedIconWrapper>
                                <SWReactIcons iconName={"social-share"} />
                            </SharedIconWrapper>
                        </PlainTooltip>
                    ) : null}
                    <HoverFooterWrapper displayed={hovered}>
                        {hoverFooter ? hoverFooter : <div />}
                        <ArrowIconWrapper>
                            <SWReactIcons
                                iconName={"arrow-right"}
                                size={"sm"}
                            />
                        </ArrowIconWrapper>
                    </HoverFooterWrapper>
                    <AddedDate displayed={!hovered}>
                        {owner && !lastSeen
                            ? `${i18n("digitalmarketing.homepage.custom_assets.sharedBy")} ${
                                  owner.firstName
                              } ${owner.lastName.substring(0, 1)}.`
                            : `${i18n("digitalmarketing.homepage.custom_assets.opened")} ${timeAgo}`}
                    </AddedDate>
                </FooterContainer>
            </Layout>
        </AssetContainer>
    );
};
